import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaSpinner,
  FaTruck,
  FaBoxOpen,
} from "react-icons/fa"; // Updated icons
import axios from "axios";
import Header from "./header";
import Footer from "./Footer";
import "./TrackOrder.css"; // We'll create this CSS file
import { Helmet } from "react-helmet";

const THEME_COLOR_PRIMARY = "#dc2626"; // A vibrant red
const THEME_COLOR_SECONDARY = "#fef2f2"; // Light background for primary

const TrackOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const [trackingData, setTrackingData] = useState([]);
  const [currentStatus, setCurrentStatus] = useState("Placed");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const statusSteps = [
    "Order Conformed",
    "Shipped",
    "Yet To Deliver",
    "Delivered",
  ];

  // Map backend statuses to our display steps
  const statusMap = {
    "order confirmed": "Order Confirmed",
    processed: "Processed",
    shipped: "Shipped",
    YettoDeliver: "Yet To Deliver",
    delivered: "Delivered",
  };

  useEffect(() => {
    fetchTracking();
  }, [orderId]);

  const fetchTracking = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/tracking`,
        {
          order_id: orderId, // 👈 sent in req.body
        },
      );

      if (res.data.success && res.data.data.length > 0) {
        const sorted = res.data.data.sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
        );

        setTrackingData(sorted);

        const latestStatus =
          statusMap[sorted[0].status.toLowerCase()] || "Order Confirmed";
        setCurrentStatus(latestStatus);
      } else {
        setError("No tracking data found for this order.");
        setCurrentStatus("Unknown");
      }
    } catch (err) {
      console.error("Tracking API error", err);
      setError("Failed to fetch tracking details. Please try again later.");
      setCurrentStatus("Failed");
    } finally {
      setLoading(false);
    }
  };

  const currentStepIndex = Math.max(statusSteps.indexOf(currentStatus), 0);

  // Determine icon for each step
  const getStepIcon = (step, index) => {
    if (index === 0) return <FaBoxOpen />; // Placed
    if (index === 1) return <FaSpinner />; // Processed
    if (index === 2) return <FaTruck />; // Shipped
    if (index === 3) return <FaCheckCircle />; // Delivered
    return null;
  };

  // Determine color for the step
  const getStepColor = (index) => {
    if (index < currentStepIndex) return THEME_COLOR_PRIMARY; // Completed steps
    if (index === currentStepIndex) return THEME_COLOR_PRIMARY; // Current step
    return "#ccc"; // Inactive steps
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Track Your Order | CGR Mart</title>

          <meta
            name="description"
            content="Track your order in real-time with CGR Mart. Get live updates on shipping status, delivery progress, and order history."
          />

          <meta
            name="keywords"
            content="track order, order tracking, shipment tracking, CGR Mart order status, delivery tracking"
          />

          <meta name="author" content="CGR Mart" />

          {/* Open Graph (Facebook / WhatsApp) */}
          <meta property="og:title" content="Track Your Order | CGR Mart" />
          <meta
            property="og:description"
            content="Easily track your order status, shipment updates, and delivery progress."
          />
          <meta property="og:type" content="website" />
          <meta
            property="og:url"
            content={`https://cgrmart.com/track-order/${orderId}`}
          />
          <meta
            property="og:image"
            content="https://cgrmart.com/assets/track-order-banner.jpg"
          />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Track Your Order | CGR Mart" />
          <meta
            name="twitter:description"
            content="Check real-time updates on your order delivery and shipment tracking."
          />
          <meta
            name="twitter:image"
            content="https://cgrmart.com/assets/track-order-banner.jpg"
          />
          
          {/* Canonical URL */}
          <link
            rel="canonical"
            href={`https://cgrmart.com/track-order/${orderId}`}
          />
        </Helmet>
        <Header />
        <main className="tracking-page-main">
          <div
            className="container d-flex justify-content-center align-items-center"
            style={{ minHeight: "300px" }}
          >
            <FaSpinner
              className="spinner-animation"
              size={40}
              color={THEME_COLOR_PRIMARY}
            />
            <p className="ml-3">Loading tracking information...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Track Your Order | CGR Mart</title>

          <meta
            name="description"
            content="Track your order in real-time with CGR Mart. Get live updates on shipping status, delivery progress, and order history."
          />

          <meta
            name="keywords"
            content="track order, order tracking, shipment tracking, CGR Mart order status, delivery tracking"
          />

          <meta name="author" content="CGR Mart" />

          {/* Open Graph (Facebook / WhatsApp) */}
          <meta property="og:title" content="Track Your Order | CGR Mart" />
          <meta
            property="og:description"
            content="Easily track your order status, shipment updates, and delivery progress."
          />
          <meta property="og:type" content="website" />
          <meta
            property="og:url"
            content={`https://cgrmart.com/track-order/${orderId}`}
          />
          <meta
            property="og:image"
            content="https://cgrmart.com/assets/track-order-banner.jpg"
          />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Track Your Order | CGR Mart" />
          <meta
            name="twitter:description"
            content="Check real-time updates on your order delivery and shipment tracking."
          />
          <meta
            name="twitter:image"
            content="https://cgrmart.com/assets/track-order-banner.jpg"
          />
          
          {/* Canonical URL */}
          <link
            rel="canonical"
            href={`https://cgrmart.com/track-order/${orderId}`}
          />
        </Helmet>
        <Header />
        <main className="tracking-page-main">
          <div className="container text-center py-5">
            <h4 style={{ color: THEME_COLOR_PRIMARY }}>Error: {error}</h4>
            <button
              onClick={() => navigate(-1)}
              className="btn mt-3"
              style={{ backgroundColor: THEME_COLOR_PRIMARY, color: "#fff" }}
            >
              Go Back
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Track Your Order | CGR Mart</title>

        <meta
          name="description"
          content="Track your order in real-time with CGR Mart. Get live updates on shipping status, delivery progress, and order history."
        />

        <meta
          name="keywords"
          content="track order, order tracking, shipment tracking, CGR Mart order status, delivery tracking"
        />

        <meta name="author" content="CGR Mart" />

        {/* Open Graph (Facebook / WhatsApp) */}
        <meta property="og:title" content="Track Your Order | CGR Mart" />
        <meta
          property="og:description"
          content="Easily track your order status, shipment updates, and delivery progress."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://cgrmart.com/track-order/${orderId}`}
        />

        {/* Twitter Card */}
        <meta name="twitter:title" content="Track Your Order | CGR Mart" />
        <meta
          name="twitter:description"
          content="Check real-time updates on your order delivery and shipment tracking."
        />
        
        {/* Canonical URL */}
        <link
          rel="canonical"
          href={`https://cgrmart.com/track-order/${orderId}`}
        />
      </Helmet>
      <Header />

      <main className="tracking-page-main">
        <div className="tracking-container">
          <div className="tracking-card">
            {/* Top Bar with Back, Order ID, and Courier Info */}
            <div className="tracking-card-header">
              <div
                onClick={() => navigate(-1)}
                className="back-button"
                style={{ color: THEME_COLOR_PRIMARY }}
              >
                <FaArrowLeft size={14} />
                <span>Back</span>
              </div>

              <h2 className="order-id-title">
                ORDER{" "}
                <span style={{ color: THEME_COLOR_PRIMARY }}>#{orderId}</span>
              </h2>

              <div className="courier-info">
                <p>
                  Tracking Number:{" "}
                  <strong style={{ color: THEME_COLOR_PRIMARY }}>
                    {trackingData[0]?.tracking_number || "N/A"}
                  </strong>
                </p>
              </div>
            </div>

            {/* Main Tracking Progress Section */}
            <div className="tracking-progress-section">
              <div className="progress-bar-container">
                {statusSteps.map((step, index) => (
                  <React.Fragment key={step}>
                    <div
                      className={`progress-step ${
                        index <= currentStepIndex ? "active" : ""
                      }`}
                    >
                      <div
                        className="step-circle"
                        style={{ borderColor: getStepColor(index) }}
                      >
                        <div
                          className="step-icon"
                          style={{ color: getStepColor(index) }}
                        >
                          {getStepIcon(step, index)}
                        </div>
                      </div>
                      <p className="step-label">{step}</p>
                    </div>
                    {index < statusSteps.length - 1 && (
                      <div
                        className={`progress-line ${
                          index < currentStepIndex ? "active" : ""
                        }`}
                        style={{
                          backgroundColor:
                            index < currentStepIndex
                              ? THEME_COLOR_PRIMARY
                              : "#e0e0e0",
                        }}
                      ></div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className="current-status-display">
                <p>Your order is currently:</p>
                <h3 style={{ color: THEME_COLOR_PRIMARY }}>{currentStatus}</h3>
              </div>
            </div>

            {/* Detailed Tracking History */}
            {trackingData.length > 0 && (
              <div className="tracking-history-section">
                <h4>Order History</h4>
                <div className="history-timeline">
                  {trackingData.map((item, index) => (
                    <div className="timeline-item" key={index}>
                      <div
                        className="timeline-dot"
                        style={{ backgroundColor: THEME_COLOR_PRIMARY }}
                      ></div>
                      <div className="timeline-content">
                        <p
                          className="status-text"
                          style={{ color: THEME_COLOR_PRIMARY }}
                        >
                          {item.status}
                        </p>
                        <p className="location-text">
                          {item.location || "N/A"}
                        </p>
                        <p className="date-text">
                          {new Date(item.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default TrackOrder;
